<template>
  <!-- 已采购记录(구매기록 表三) — 商品求购 탭. 订单列表의 엑셀 버튼이었다가 화면 표로 옮겼다(2026-10-07 YZY).
       값은 서버(/api/reqpurchase/purchasedrecord)가 채우고, 칸 배치·값 꺼내기는 엑셀과 같은 purchasedRecordXlsx.js COLUMNS 를 쓴다 -->
  <div class="purchased-record">
    <div class="d-flex flex-wrap align-items-center mb-2" style="gap:6px">
      <span class="small">求购日期<small>(요청일)</small></span>
      <input type="date" v-model="startdate" class="form-control form-control-sm" style="width:150px">
      <span>~</span>
      <input type="date" v-model="enddate" class="form-control form-control-sm" style="width:150px">
      <span class="small ml-2">货主<small>(업체)</small></span>
      <input type="text" v-model.trim="companyid" list="purchasedRecordCompanyList" placeholder="全部(전체)"
             class="form-control form-control-sm" style="width:160px" @keyup.enter="search">
      <datalist id="purchasedRecordCompanyList">
        <option v-for="c in companyOptions" :key="c" :value="c"></option>
      </datalist>
      <b-form-select v-model="customsFilter" :options="customsOptions" size="sm" style="width:170px"></b-form-select>
      <b-button size="sm" variant="primary" @click="search" :disabled="loading">
        <b-spinner v-if="loading" small class="mr-1"></b-spinner>查询<small>(조회)</small>
      </b-button>
      <b-button size="sm" variant="success" @click="downloadExcel" :disabled="!viewRows.length">⬇ Excel<small>(表三 엑셀 다운로드)</small></b-button>
      <span class="legend legend--client">蓝色单独(화주단독)</span>
      <span class="legend legend--kupass">橙色单独(쿠패스단독·식검)</span>
      <span class="legend">综合清关(통합통관)</span>
    </div>
    <div class="small text-muted mb-2">
      按求购日期·货主显示全部已付款(含已入库) / 요청일·업체 조건으로 결제완료 전부(입고 후 포함).
      估算 = 报价未发行，按报价规则计算(견적 미발행 — 견적 규칙으로 계산한 값)
    </div>
    <div v-if="truncated" class="alert alert-warning py-1 small mb-2">
      结果过多，仅显示前30000条，请缩小日期或选择货主(3만 건까지만 — 날짜를 좁히거나 업체를 고르세요)
    </div>

    <CDataTable
      storage-key="PurchasedRecordView" :headers="headers" :items="items" item-key="_id"
      :items-per-page="50" fixedColumns="D" height="620px">
      <template v-for="c in columns" v-slot:[c.col]="{ item }">
        <span :key="c.col" class="pr-cell" :class="cellClass(c, item)" :title="c.col === 'D' || c.col === 'R' ? item[c.col] : null">{{ cellText(c, item[c.col]) }}</span>
      </template>
    </CDataTable>

    <!-- 합계 — 위 조회 조건(기간·货主·통관방식) 전체 기준. 표 안 검색은 반영하지 않는다 -->
    <div v-if="viewRows.length" class="totals mt-2">
      <b>合计<small>(합계)</small></b>
      <span>{{ totals.count.toLocaleString() }}件</span>
      <span>采购数量<small>(구매수량)</small> {{ totals.qty.toLocaleString() }}</span>
      <span>协商前<small>(협상전)</small> ¥{{ money2(totals.pre) }}</span>
      <span>协商后<small>(협상후)</small> ¥{{ money2(totals.post) }}</span>
      <span class="legend legend--client">M列 货主单独<small>(화주단독)</small> ₩{{ won(totals.krwClient) }}</span>
      <span class="legend legend--kupass">N列 Coupass清关·综合<small>(쿠패스·통합통관)</small> ₩{{ won(totals.krwOther) }}</span>
      <span>增值税<small>(부가세)</small> ₩{{ won(totals.vat) }}</span>
      <span>最终合计<small>(최종금액합계)</small> <b>₩{{ won(totals.final) }}</b></span>
      <span v-if="totals.refundCny">退款<small>(환불)</small> ¥{{ money2(totals.refundCny) }} / ₩{{ won(totals.refundKrw) }}</span>
      <span v-if="totals.est" class="text-warning">估算 {{ totals.est }}件</span>
      <span v-if="totals.check" class="text-danger">协商后金额待核对<small>(협상후 금액 확인 필요)</small> {{ totals.check }}件</span>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import CDataTable from '@/components/common/CDataTable';
import { COLUMNS, cellValue, cellText, writePurchasedRecordXlsx, purchasedRecordFileName } from './purchasedRecordXlsx';

// 表三 칸 제목의 중국어(화면 표시용)
const HEADER_ZH = {
  A: '申请日期', B: '公司名', C: '申请人', D: '商品名', E: '采购数量',
  F: '采购单价(CNY)', G: '运费(CNY)', H: '总金额(CNY)',
  I: '协商后采购单价(CNY)', J: '协商后运费(CNY)', K: '协商后1688付款金额(CNY)',
  L: '协商折扣率(%)', M: '付款金额(KRW)', N: 'Coupass清关付款金额(KRW)', O: '增值税', P: '最终合计(KRW)',
  Q: '适用汇率', R: '备注', T: '条码', U: '订单号', X: '采购商品名', Y: '总采购数量',
  AA: '退货日期', AB: '公司名', AC: '数量', AD: '退款金额(CNY)', AE: '退货运费',
  AF: '退货付款金额(CNY)', AG: '退货付款金额(KRW)', AH: '适用汇率', AI: '适用手续费率', AJ: '退货原因', AK: '付款日'
};

function ymd(d) {
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
}

export default {
  name: 'PurchasedRecordView',
  components: { CDataTable },
  data() {
    const today = new Date();
    return {
      startdate: ymd(new Date(today.getTime() - 14 * 24 * 60 * 60 * 1000)),
      enddate: ymd(today),
      companyid: '',
      customsFilter: '',
      customsOptions: [
        { value: '', text: '全部清关方式(통관방식 전체)' },
        { value: 'client', text: '货主单独(화주단독)' },
        { value: 'kupass', text: 'Coupass单独(쿠패스단독)' },
        { value: 'integrated', text: '综合清关(통합통관)' },
        { value: 'none', text: '未定(통관방식 없음)' }
      ],
      companyOptions: [],
      // 서버 행(조회 조건 그대로). 읽기 전용이라 반응형을 걸지 않는다(수천 건)
      rows: Object.freeze([]),
      truncated: false,
      loading: false,
      // 엑셀 머리글에 적는 조회 조건 — 마지막으로 조회한 값
      info: null
    };
  },
  computed: {
    columns() { return COLUMNS; },
    // 열 = 表三 A~AO 그대로. 제목이 겹치는 칸(업체명·적용환율)이 있어 열 문자를 앞에 붙인다
    // 화면 머리글만 中文(한국어) — 엑셀 머리글(COLUMNS.title)은 그대로. 이미 중국어가 있는 칸은 원래 제목
    headers() {
      return COLUMNS.map(c => {
        const zh = HEADER_ZH[c.col];
        return { text: c.col + ' ' + (zh ? zh + '\n(' + c.title.replace(/\n/g, ' ') + ')' : c.title), value: c.col, isSlot: true };
      });
    },
    viewRows() {
      const f = this.customsFilter;
      if (!f) return this.rows;
      return this.rows.filter(r => (f === 'none' ? !r.customsType : r.customsType === f));
    },
    // 칸 값은 엑셀과 같은 key 로 꺼낸다(정렬·검색이 숫자·글자 그대로 되게 표시 서식은 슬롯에서)
    items() {
      return Object.freeze(this.viewRows.map(r => {
        const it = { _id: r._id, _ct: r.customsType || '', _est: !!r.估算, _chk: !!r.협상후수량불일치 };
        COLUMNS.forEach(c => { it[c.col] = cellValue(c, r); });
        return Object.freeze(it);
      }));
    },
    totals() {
      const n = v => (Number.isFinite(Number(v)) ? Number(v) : 0);
      const t = { count: 0, qty: 0, pre: 0, post: 0, krwClient: 0, krwOther: 0, vat: 0, final: 0, refundCny: 0, refundKrw: 0, est: 0, check: 0 };
      this.viewRows.forEach(r => {
        t.count++;
        t.qty += n(r.구매수량);
        t.pre += n(r.협상전_총금액);
        t.post += n(r.협상후_총금액);
        if (r.customsType === 'client') t.krwClient += n(r.결제금액_KRW); else t.krwOther += n(r.결제금액_KRW);
        t.vat += n(r.부가세);
        t.final += n(r.최종금액합계_KRW);
        if (r.refund) { t.refundCny += n(r.refund.환불CNY); t.refundKrw += n(r.refund.환불KRW); }
        if (r.估算) t.est++;
        if (r.협상후수량불일치) t.check++;
      });
      return t;
    }
  },
  mounted() {
    this.search();
  },
  methods: {
    cellText,
    money2(v) { return (Math.round(v * 100) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }); },
    won(v) { return Math.round(v).toLocaleString(); },
    // 통관방식 색 — 엑셀과 같은 자리: 화주단독 = M 칸 蓝, 쿠패스단독 = N 칸 橙. 비고의 估算·확인 필요는 눈에 띄게
    cellClass(c, item) {
      if (c.col === 'M' && item._ct === 'client' && item.M != null) return 'cell-client';
      if (c.col === 'N' && item._ct === 'kupass' && item.N != null) return 'cell-kupass';
      if (c.col === 'R') return ['pr-cell--clip', item._chk ? 'text-danger' : (item._est ? 'text-warning-dark' : '')];
      if (c.col === 'D') return 'pr-cell--clip';
      return '';
    },
    async search() {
      if (this.loading) return;
      this.loading = true;
      try {
        const cond = { companyid: this.companyid, start: this.startdate, end: this.enddate };
        const { data } = await axios.post('/api/reqpurchase/purchasedrecord', cond);
        if (data.요청결과 !== '성공') {
          alert('查询失败(조회 실패): ' + ((data.error && data.error.errorMessage) || ''));
          return;
        }
        this.rows = Object.freeze(data.rows || []);
        this.truncated = !!data.truncated;
        this.info = cond;
        const set = new Set(this.companyOptions);
        this.rows.forEach(r => { if (r.업체명) set.add(r.업체명); });
        this.companyOptions = [...set].sort();
      } catch (e) {
        console.error('已采购记录 조회 오류:', e);
        alert('查询错误(조회 오류): ' + e.message);
      } finally {
        this.loading = false;
      }
    },
    // 지금 보이는 조회 결과 그대로 表三 엑셀로
    downloadExcel() {
      try {
        const info = this.info || { start: this.startdate, end: this.enddate, companyid: this.companyid };
        const blob = new Blob([writePurchasedRecordXlsx(this.viewRows, info)], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = purchasedRecordFileName(info);
        document.body.appendChild(a);
        a.click();
        setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 1000);
      } catch (e) {
        console.error('已采购记录 엑셀 오류:', e);
        alert('导出错误(내보내기 오류): ' + e.message);
      }
    }
  }
};
</script>

<style scoped>
.legend {
  display: inline-block;
  padding: 1px 6px;
  border: 1px solid #ccc;
  border-radius: 3px;
  font-size: 0.8rem;
}
.legend--client { background: #0070c0; color: #fff; border-color: #0070c0; }
.legend--kupass { background: #ffc000; border-color: #ffc000; }
/* 한 행 한 줄 — 수천 행을 훑어보는 표라 줄바꿈 대신 상품명·비고는 잘라 보이고 마우스를 올리면 전체 */
.pr-cell { white-space: nowrap; }
.pr-cell--clip { display: inline-block; max-width: 260px; overflow: hidden; text-overflow: ellipsis; vertical-align: middle; }
.cell-client { background: #0070c0; color: #fff; padding: 1px 4px; }
.cell-kupass { background: #ffc000; padding: 1px 4px; }
.text-warning-dark { color: #b8860b; }
.totals {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 14px;
  align-items: center;
  font-size: 0.85rem;
  padding: 6px 10px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  background: #f8f9fa;
}
</style>
